import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { DataRoomRedFlag, RedFlagStatus } from "@/models/data-room/DataRoomRedFlag";
import { DataRoomLazoModalSx } from "@/pages/OneDataRoomPage";
import RedFlagService from "@/services/RedFlagService";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, MenuItem, SxProps, Typography } from "@mui/material";
import {
  CellContext,
  SortDirection,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { StyledInputField } from "../fundraising/advanced-search/AdvancedSearchFilters";
import CommonButton from "../utils/CommonButton";
import { FileTableHeaderCellContainer, FileTableMenu, FileTableMoreHoriz, FilesStyledTable } from "../utils/FileTable/FileTableStyles";
import LazoModal from "../utils/LazoModal";
import CheckCircleIcon from "../utils/icons/CheckCircleIcon";
import ExclamationIcon from "../utils/icons/ExclamationIcon";

type Props = {
  flags: DataRoomRedFlag[];
  onFlagsUpdate: (updated: DataRoomRedFlag[]) => void;
  hideFix?: boolean;
  hideHeader?: boolean;
};

function getIcon(status: RedFlagStatus) {
  switch (status) {
    case RedFlagStatus.FIXED:
    case RedFlagStatus.DISMISSED:
      return <CheckCircleIcon />;
    case RedFlagStatus.CREATED:
    case RedFlagStatus.ASSIGNED:
      return <ExclamationIcon style={{ fill: Colors.HEART_RED }} />;
    default:
      return <></>;
  }
}

const columnHelper = createColumnHelper<DataRoomRedFlag>();

function DataRoomRedFlagsTable(props: Props) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "name-col", desc: false }]);
  const [menuFlag, setMenuFlag] = useState<null | DataRoomRedFlag>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const [renaming, setRenaming] = useState<boolean>(false);
  const [openRenameModal, setOpenRenameModal] = useState<boolean>(false);
  const [name, setName] = useState<string>("");

  const [deleting, setDeleting] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [updating, setUpdating] = useState<{ [k: string]: boolean }>({});

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name-col",
        cell: (info) => (
          <Box display="flex" alignItems="center" gap="0.5rem">
            {getIcon(info.row.original.status)}
            <Typography>{info.getValue()}</Typography>
          </Box>
        ),
        header: "Flag",
        meta: {
          width: "fit-content",
        },
      }),
      {
        id: "filename-col",
        accessorFn: (row: DataRoomRedFlag) => (row.file ? row.file.name : "-"),
        cell: (info: CellContext<DataRoomRedFlag, string>) => (
          <Box display="flex" alignItems="center" gap="0.5rem">
            <Typography>{info.getValue()}</Typography>
          </Box>
        ),
        header: "File",
        meta: {
          width: "fit-content",
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: props.flags,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
    },
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
  });

  function handleMoreClick(ev: React.MouseEvent<HTMLElement>, flag: DataRoomRedFlag) {
    setMenuAnchor(ev.currentTarget);
    setMenuFlag(flag);
  }

  function handleMenuClose() {
    setMenuAnchor(null);
  }

  function handleRenameClick() {
    setName("");
    setOpenRenameModal(true);
  }

  function handleKeyPress(ev: React.KeyboardEvent<HTMLDivElement>) {
    if (ev.key === "Enter") {
      handleRename();
    }
  }

  function handleRename() {
    if (!name || name === "") return;

    setRenaming(true);
    RedFlagService.update(menuFlag.externalId, name, menuFlag.status)
      .then((r) => {
        r.onSuccess((updated) => {
          props.onFlagsUpdate(
            props.flags.map((f) => {
              if (f.externalId === updated.externalId) {
                return updated;
              }
              return f;
            })
          );
          setOpenRenameModal(false);
          setMenuAnchor(null);
        });
        r.onError((e) => {
          NotificationService.error("Error renaming, please try again");
          console.log("error: " + e);
        });
      })
      .then(() => setRenaming(false));
  }

  function handleDeleteClick() {
    setOpenDeleteModal(true);
  }

  function handleDelete() {
    setDeleting(true);
    RedFlagService.delete(menuFlag.externalId)
      .then((r) => {
        r.onSuccess(() => {
          props.onFlagsUpdate(props.flags.filter((f) => f.externalId !== menuFlag.externalId));
          setOpenDeleteModal(false);
          setMenuAnchor(null);
        });
        r.onError((e) => {
          NotificationService.error("Error deleting, please try again");
          console.log("error: " + e);
        });
      })
      .finally(() => setDeleting(false));
  }

  function handleStateUpdate(flag: DataRoomRedFlag, state: RedFlagStatus) {
    if (updating[flag.externalId]) return;
    setUpdating((prev) => ({ ...prev, [flag.externalId]: true }));
    RedFlagService.update(flag.externalId, flag.name, state)
      .then((r) => {
        r.onSuccess((updated) => {
          props.onFlagsUpdate(
            props.flags.map((f) => {
              if (f.externalId === updated.externalId) {
                return updated;
              }
              return f;
            })
          );
        });
        r.onError((e) => {
          NotificationService.error("Error updating your flag. Please try again!");
          console.log("error: ", e);
        });
      })
      .finally(() => setUpdating((prev) => ({ ...prev, [flag.externalId]: false })));
  }

  return (
    <>
      <FilesStyledTable>
        {!props.hideHeader && (
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: header.column.columnDef.meta?.width || "auto",
                      minWidth: header.column.columnDef.meta?.minWidth || "auto",
                    }}
                  >
                    <FileTableHeaderCellContainer onClick={header.column.getToggleSortingHandler()}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() ? (
                        (header.column.getIsSorted() as SortDirection) === "asc" ? (
                          <KeyboardArrowDown />
                        ) : (
                          <KeyboardArrowUp />
                        )
                      ) : null}
                    </FileTableHeaderCellContainer>
                  </th>
                ))}
                {/* Placeholder: this determines the width of the last <td/> */}
                <th style={{ width: "200px" }}></th>
              </tr>
            ))}
          </thead>
        )}
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
              {/* ACTIONS */}
              <td key={row.original.externalId + "-actions"}>
                <Box
                  sx={{
                    display: "flex",
                    gap: "1rem",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {![RedFlagStatus.FIXED, RedFlagStatus.DISMISSED].includes(row.original.status) ? (
                    <Box
                      sx={{ width: "fit-content", alignItems: "center" }}
                      onClick={() => handleStateUpdate(row.original, RedFlagStatus.DISMISSED)}
                    >
                      {updating[row.original.externalId] && <Spinner size={"0.875rem"} style={{ color: Colors.LAZO_PURPLE }} />}
                      {!updating[row.original.externalId] && (
                        <Typography
                          sx={{
                            border: `1px solid transparent`,
                            transition: "border 0.2s ease",
                            ":hover": {
                              cursor: "pointer",
                              borderBottom: `1px solid ${Colors.BLACK_2}`,
                            },
                          }}
                        >
                          Dismiss
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{ width: "fit-content", alignItems: "center" }}
                      onClick={() => handleStateUpdate(row.original, RedFlagStatus.CREATED)}
                    >
                      {updating[row.original.externalId] && <Spinner size={"0.875rem"} style={{ color: Colors.LAZO_PURPLE }} />}
                      {!updating[row.original.externalId] && (
                        <Typography
                          sx={{
                            border: `1px solid transparent`,
                            transition: "border 0.2s ease",
                            ":hover": {
                              cursor: "pointer",
                              borderBottom: `1px solid ${Colors.BLACK_2}`,
                            },
                          }}
                        >
                          Re-Flag
                        </Typography>
                      )}
                    </Box>
                  )}
                  {!props.hideFix && row.original.status !== RedFlagStatus.FIXED && (
                    <CommonButton purpleBorderVariant sx={FixButtonStyle}>
                      <Typography display="flex" alignItems="center" fontSize="0.75rem" fontWeight="500" lineHeight="18px">
                        Fix
                      </Typography>
                    </CommonButton>
                  )}
                  <Box
                    sx={{
                      maxWidth: "fit-content",
                      alignItems: "center",
                      display: "flex",
                    }}
                    onClick={(ev) => handleMoreClick(ev, row.original)}
                  >
                    <FileTableMoreHoriz />
                  </Box>
                </Box>
              </td>
            </tr>
          ))}
        </tbody>
      </FilesStyledTable>
      <FileTableMenu anchorEl={menuAnchor} open={menuOpen} onClose={handleMenuClose} transitionDuration={0}>
        <MenuItem disabled={renaming} onClick={handleRenameClick}>
          <Typography>Rename</Typography>
        </MenuItem>
        {false && (
          <MenuItem>
            <Typography>Create a Task</Typography>
          </MenuItem>
        )}
        <MenuItem disabled={deleting} onClick={handleDeleteClick}>
          <Typography color="red">Delete</Typography>
        </MenuItem>
      </FileTableMenu>
      <LazoModal
        sx={DataRoomLazoModalSx}
        open={openRenameModal}
        title={"Rename Red Flag"}
        handleClose={() => setOpenRenameModal(false)}
        buttons={[
          <CommonButton style={{ borderRadius: "0.5rem" }} purpleBorderVariant onClick={() => setOpenRenameModal(false)}>
            Cancel
          </CommonButton>,
          <CommonButton style={{ borderRadius: "0.5rem" }} onClick={handleRename} disabled={renaming || !name || name === ""}>
            {!renaming ? "Save" : <Spinner size={"1.5rem"} />}
          </CommonButton>,
        ]}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            flexDirection: "column",
            gap: "0.25rem",
          }}
        >
          <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" paddingLeft="0.5rem" color={Colors.LIGHT_GRAY_8A8E93}>
            Name
          </Typography>
          <StyledInputField
            sx={{ "& .MuiInputBase-root": { padding: 0 } }}
            hideIcon
            value={name}
            onChange={(v) => setName(v.currentTarget.value)}
            placeholder="Type new name..."
            onKeyDown={handleKeyPress}
          />
        </Box>
      </LazoModal>
      <LazoModal
        sx={DataRoomLazoModalSx}
        open={openDeleteModal}
        title={"Are you sure you want to delete this Red Flag?"}
        handleClose={() => setOpenDeleteModal(false)}
        buttons={[
          <CommonButton style={{ borderRadius: "0.5rem" }} purpleBorderVariant onClick={() => setOpenDeleteModal(false)}>
            Cancel
          </CommonButton>,
          <CommonButton
            style={{
              borderRadius: "0.5rem",
              backgroundColor: Colors.HEART_RED,
            }}
            onClick={handleDelete}
            disabled={deleting}
          >
            {!deleting ? "Delete" : <Spinner size={"1.5rem"} />}
          </CommonButton>,
        ]}
      >
        <Typography fontSize="0.75rem" fontWeight="400" lineHeight="18px" color={Colors.LIGHT_GRAY_8A8E93}>
          This action will be permanent.
        </Typography>
      </LazoModal>
    </>
  );
}

const FixButtonStyle: SxProps = {
  display: "flex",
  alignItems: "center",
  borderRadius: "0.25rem",
  height: "1.5rem",
  minWidth: "fit-content",
  padding: "0 0.5rem 0 0.5rem",
};

export default DataRoomRedFlagsTable;
